import React from 'react';
import { ExternalLink, Star, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

export const ProductShowcase = ({ product, selectedVariant, setSelectedVariant, hideHeader = false }) => {
  const handleBuyOnAmazon = () => {
    const url = product?.buy_url || 'https://amazon.com';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!product || !selectedVariant) return null;

  return (
    <section 
      id="products" 
      className="relative overflow-hidden"
    >
      <style jsx>{`
        #products {
          padding-top: 7rem;
          padding-bottom: 8rem;
        }
        
        @media (max-width: 768px) {
          #products {
            padding-top: 1rem;
            padding-bottom: 1rem;
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header - Centered */}
        {!hideHeader && (
          <div className="text-center product-section-spacing">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white px-6 py-2 rounded-full border border-orange-500/50 shadow-lg shadow-orange-500/20 mb-4">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="gaming-text font-bold text-orange-400 uppercase tracking-widest" style={{ fontSize: '12px' }}>Our Arsenal</span>
            </div>
            
            {/* Main Heading */}
            <h2 className="gaming-title text-5xl sm:text-6xl font-bold mb-2 uppercase" style={{ color: '#FF4A00' }}>
              <span className="text-orange-500">PREMIUM</span> CARD SLEEVES
            </h2>
            
            {/* Subtitle with decorative lines - reduced spacing */}
            <div className="flex items-center justify-center space-x-4">
              <div className="h-px w-20 bg-gradient-to-r from-transparent to-orange-500"></div>
              <p className="gaming-text text-gray-900 uppercase tracking-wide" style={{ fontSize: '18px' }}>Multiple Colors Available</p>
              <div className="h-px w-20 bg-gradient-to-l from-transparent to-orange-500"></div>
            </div>
          </div>
        )}

        {/* Product Display */}
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-12">
          {/* Left Side - Square Image with Angled Color Background */}
          <div className="space-y-6">
            <div className="relative max-w-lg mx-auto">
              {/* Angled colored square behind - centered with specific dimensions */}
              <div 
                className="absolute rounded-2xl transform rotate-6 transition-all duration-500"
                style={{ 
                  backgroundColor: selectedVariant.color,
                  filter: 'blur(4px)',
                  opacity: 0.8,
                  zIndex: -1,
                  width: '93%',
                  height: '98%',
                  top: '1%',
                  left: '4%',
                  transform: 'rotate(6deg)'
                }}
              ></div>
              
              {/* Main square image placeholder */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                {/* Image - Shows selected variant */}
                <div className="w-full aspect-square bg-gray-800 flex items-center justify-center relative">
                  <img 
                    src={selectedVariant.image_url}
                    alt={selectedVariant.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Color Selection Thumbnails - Placeholders */}
            <div className="space-y-6 mt-12">
              {/* Select Color with decorative lines */}
              <div className="flex items-center justify-center space-x-4">
                <div className="h-px w-16 bg-gradient-to-r from-transparent to-gray-500"></div>
                <h3 className="gaming-text font-bold text-gray-900 uppercase tracking-wide" style={{ fontSize: '16px' }}>Select Color</h3>
                <div className="h-px w-16 bg-gradient-to-l from-transparent to-gray-500"></div>
              </div>
              
              <div className="grid grid-cols-5 gap-3 max-w-lg mx-auto">
                {product.variants?.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariant(variant)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      selectedVariant.id === variant.id
                        ? 'border-orange-500 scale-110'
                        : 'border-gray-600 hover:border-orange-400'
                    }`}
                  >
                    {/* Product Image Thumbnail */}
                    <div className="w-full h-full relative">
                      <img 
                        src={variant.image_url} 
                        alt={variant.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Product Details */}
          <div className="space-y-6">
            {/* Product Title */}
            <div>
              <h3 className="gaming-title text-3xl sm:text-4xl font-bold mb-2 uppercase leading-tight">
                <span className="text-orange-500">PREMIUM MTG</span><br/>
                <span className="text-gray-900">CARD SLEEVES</span>
              </h3>
              <p className="gaming-text text-xl font-semibold text-orange-500 uppercase">
                {selectedVariant.name}
              </p>
            </div>

            {/* Rating - Conditional */}
            {product.show_reviews && (
              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="gaming-text ml-2 text-gray-900" style={{ fontSize: '16px' }}>(250+ REVIEWS)</span>
              </div>
            )}

            {/* Price */}
            <div className="rounded-lg p-6" style={{ backgroundColor: 'rgba(255, 74, 0, 0.15)', border: '1px solid rgba(255, 74, 0, 0.3)' }}>
              <div className="flex items-baseline space-x-3 mb-2">
                <span className="gaming-title text-5xl font-bold" style={{ color: '#FF4A00' }}>
                  ${product.price.toFixed(2)}
                </span>
                <span className="gaming-text text-lg text-gray-900 uppercase">Per Pack</span>
              </div>
              {product.sleeves_included_text && (
                <p className="gaming-text text-gray-900 uppercase" style={{ fontSize: '16px' }}>
                  {product.sleeves_included_text}
                </p>
              )}
            </div>

            {/* Key Features */}
            {product.features && product.features.length > 0 && (
              <div className="space-y-4">
                <h4 className="gaming-title font-bold text-xl uppercase tracking-wide" style={{ color: '#ff8c5a' }}>Key Features</h4>
                <div className="space-y-3">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="gaming-text text-gray-900" style={{ fontSize: '16px' }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Button */}
            <div className="pt-4">
              <Button
                onClick={handleBuyOnAmazon}
                size="lg"
                className="gaming-text w-full bg-gradient-to-r from-[#FF4A00] to-[#FF4A00] hover:from-orange-500 hover:to-orange-600 text-white text-lg py-6 font-bold uppercase tracking-wider shadow-2xl shadow-orange-600/50 transition-all duration-300 transform hover:scale-105"
                style={{ fontSize: '18px' }}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Buy on Amazon
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
